/**
 * Subscriptions & Billing Schema
 * Subscription plans, entitlements, and feature usage tracking
 */

import { pgTable, uuid, varchar, text, timestamp, integer, boolean } from 'drizzle-orm/pg-core';
import { relations } from 'drizzle-orm';
import { profiles } from './profiles';

// =============================================================================
// Subscriptions
// =============================================================================
export const subscriptions = pgTable('subscriptions', {
  id: uuid('id').primaryKey().defaultRandom(),
  userId: uuid('user_id').notNull(),
  tier: varchar('tier', { length: 50 }).notNull(), // 'trial', 'startup', 'enterprise', 'global'
  status: varchar('status', { length: 50 }).notNull(), // 'active', 'trialing', 'canceled', 'past_due'
  stripePriceId: varchar('stripe_price_id', { length: 255 }),
  stripeSubscriptionId: varchar('stripe_subscription_id', { length: 255 }),
  stripeCustomerId: varchar('stripe_customer_id', { length: 255 }),
  currentPeriodStart: timestamp('current_period_start'),
  currentPeriodEnd: timestamp('current_period_end'),
  cancelAtPeriodEnd: boolean('cancel_at_period_end').default(false),
  metadata: text('metadata'), // JSON
  createdAt: timestamp('created_at').defaultNow().notNull(),
  updatedAt: timestamp('updated_at').defaultNow().notNull(),
});

// =============================================================================
// User Entitlements
// =============================================================================
export const userEntitlements = pgTable('user_entitlements', {
  id: uuid('id').primaryKey().defaultRandom(),
  userId: uuid('user_id').notNull(),
  entitlementKey: varchar('entitlement_key', { length: 255 }).notNull(),
  grantedAt: timestamp('granted_at').defaultNow().notNull(),
  expiresAt: timestamp('expires_at'),
  source: varchar('source', { length: 100 }), // 'subscription', 'manual', 'promotion'
  metadata: text('metadata'), // JSON
  createdAt: timestamp('created_at').defaultNow().notNull(),
});

// =============================================================================
// Feature Usage
// =============================================================================
export const featureUsage = pgTable('feature_usage', {
  id: uuid('id').primaryKey().defaultRandom(),
  userId: uuid('user_id').notNull(),
  featureId: varchar('feature_id', { length: 255 }).notNull(),
  count: integer('count').default(1).notNull(),
  metadata: text('metadata'), // JSON
  createdAt: timestamp('created_at').defaultNow().notNull(),
});

// =============================================================================
// Trial Users
// =============================================================================
export const trialUsers = pgTable('trial_users', {
  id: uuid('id').primaryKey().defaultRandom(),
  trialId: varchar('trial_id', { length: 255 }).notNull().unique(),
  email: varchar('email', { length: 255 }),
  featuresUsed: text('features_used'), // JSON array
  trialStarted: timestamp('trial_started'),
  trialExpires: timestamp('trial_expires'),
  status: varchar('status', { length: 50 }).notNull().default('anonymous'),
  createdAt: timestamp('created_at').defaultNow().notNull(),
  updatedAt: timestamp('updated_at').defaultNow().notNull(),
});

// =============================================================================
// Relations
// =============================================================================
export const subscriptionsRelations = relations(subscriptions, ({ one }) => ({
  user: one(profiles, {
    fields: [subscriptions.userId],
    references: [profiles.id],
  }),
}));

export const userEntitlementsRelations = relations(userEntitlements, ({ one }) => ({
  user: one(profiles, {
    fields: [userEntitlements.userId],
    references: [profiles.id],
  }),
}));

export const featureUsageRelations = relations(featureUsage, ({ one }) => ({
  user: one(profiles, {
    fields: [featureUsage.userId],
    references: [profiles.id],
  }),
}));

// =============================================================================
// Types
// =============================================================================
export type Subscription = typeof subscriptions.$inferSelect;
export type NewSubscription = typeof subscriptions.$inferInsert;
export type UserEntitlement = typeof userEntitlements.$inferSelect;
export type NewUserEntitlement = typeof userEntitlements.$inferInsert;
export type FeatureUsage = typeof featureUsage.$inferSelect;
export type NewFeatureUsage = typeof featureUsage.$inferInsert;
export type TrialUserRecord = typeof trialUsers.$inferSelect;
export type NewTrialUserRecord = typeof trialUsers.$inferInsert;
